/**
 * VKStatusDot — sidebar-embedded live indicator for VibeKonnect coordination state.
 *
 * Polls /api/vibekonnect/coordination-status every 45s. Shows a small pulsing
 * dot that reflects the current verdict:
 *   ✅ green   → GO (safe to commit + deploy)
 *   ⚠️ amber  → PROCEED WITH CARE (warnings present)
 *   🛑 red    → STOP (deployment locked)
 *
 * Click → navigates to /admin/vibekonnect-coordination for the full breakdown.
 * Super-admin only (the endpoint 403s for everyone else).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const VERDICT_TO_STYLE = {
  GO: {
    dotClass: 'bg-emerald-500',
    ringClass: 'ring-emerald-400/40',
    pulseClass: 'bg-emerald-500/50',
    label: '✅ VK: GO',
    tooltipPrefix: 'VibeKonnect: safe to commit + deploy',
  },
  'PROCEED WITH CARE': {
    dotClass: 'bg-amber-500',
    ringClass: 'ring-amber-400/40',
    pulseClass: 'bg-amber-500/50',
    label: '⚠️ VK: CAUTION',
    tooltipPrefix: 'VibeKonnect: proceed with care — see dashboard',
  },
  STOP: {
    dotClass: 'bg-rose-500',
    ringClass: 'ring-rose-400/40',
    pulseClass: 'bg-rose-500/60',
    label: '🛑 VK: LOCKED',
    tooltipPrefix: 'VibeKonnect: DEPLOYMENT LOCKED — click for fix',
  },
};

export default function VKStatusDot({ userRole }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (userRole !== 'super_admin') return;
    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/api/vibekonnect/coordination-status`);
        if (!cancelled) {
          setState(data);
          setError(false);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    };

    load();
    const t = setInterval(load, 45000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [userRole]);

  if (userRole !== 'super_admin') return null;
  if (error || !state) return null;

  const verdict = state.verdict?.verdict || 'STOP';
  const style = VERDICT_TO_STYLE[verdict] || VERDICT_TO_STYLE.STOP;
  const shouldPulse = verdict !== 'GO';

  const tooltip = `${style.tooltipPrefix}\n\n${state.verdict?.headline || ''}\n${state.verdict?.reason || ''}\nClick for details.`;

  return (
    <button
      type="button"
      onClick={() => navigate('/admin/vibekonnect-coordination')}
      className="w-full flex items-center justify-center gap-2 px-2.5 py-1.5 mb-2 rounded-md text-[11px] font-body text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
      title={tooltip}
      data-testid={`vk-status-dot-${verdict.toLowerCase().replace(/\s/g, '-')}`}
    >
      <span className="relative flex h-2.5 w-2.5">
        {shouldPulse && (
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full ${style.pulseClass} opacity-75`}
          />
        )}
        <span
          className={`relative inline-flex rounded-full h-2.5 w-2.5 ${style.dotClass} ring-2 ${style.ringClass}`}
        />
      </span>
      <span className="tracking-wide" data-testid="vk-status-dot-label">{style.label}</span>
    </button>
  );
}
